/**
 * @file Native Bridge Actions
 */

/* eslint-disable camelcase, @typescript-eslint/camelcase */
import { Reaction } from '@padlet/arvo'
import { buildUrlFromPath } from 'bits/location'
import { ActionMessage } from 'native_bridge/types'
import { Cid, Comment, Id, Post, Section, User, UserId, WallReactionData } from 'types'

export const OPEN_LINK = 'open_link'
export function openLink(url, target, contentCategory, contentSubcategory): ActionMessage {
  return {
    message_type: OPEN_LINK,
    link_type: target,
    url: url,
    content_category: contentCategory,
    content_subcategory: contentSubcategory,
  }
}

export const EDIT_POST = 'edit_post'
export function editPost(postAttributes): ActionMessage {
  return {
    message_type: EDIT_POST,
    postAttributes: postAttributes,
  }
}

export const UPDATE_BACKGROUND = 'padlet_background_update'
export function updateBackground(backgroundAttributes): ActionMessage {
  const nativeAppBgAttr = {
    ...backgroundAttributes,
    image_color: backgroundAttributes.dominant_color_as_rgb,
    image_luminance: backgroundAttributes.luminance_name,
  }
  return {
    message_type: UPDATE_BACKGROUND,
    background_attributes: nativeAppBgAttr,
  }
}

interface ExpandPostManifest {
  posts: { subject: string | null; url: string }
  post_index: number
}
/**
 * An expanded post manifest describes to the mobile app
 * how to display an expanded post. It also provides information
 * on how to iterate through all posts and expand each one of them.
 */
function createExpandedPostManifest({ expandedPost, wishFrameUrlTemplate, allPosts, wall }): ExpandPostManifest {
  const currentIndex = allPosts.findIndex((post) => {
    return expandedPost.id === post.id
  })
  const postManifests = allPosts.map((post) => {
    const url = wishFrameUrlTemplate
      .replace('_host_', window.location.hostname)
      .replace('_wallPublicKey_', wall.public_key)
      .replace('_wishId_', post.id.toString())
    return {
      subject: post.subject || null,
      url,
    }
  })
  return { posts: postManifests, post_index: currentIndex }
}

export const EXPAND_POST = 'show_expanded_post'
export function expandPost(expandedPostRequiredAttributes): ActionMessage {
  const { window, allPosts, expandedPost } = expandedPostRequiredAttributes
  const expandedPostIndex = allPosts.map((w) => w.id).indexOf(expandedPost.id)
  window.location.href = buildUrlFromPath(`/ios-app/expand-post?selected_index=${expandedPostIndex}`)

  // End Hack
  const expandedPostManifest = createExpandedPostManifest(expandedPostRequiredAttributes)
  return {
    message_type: EXPAND_POST,
    ...expandedPostManifest,
  }
}

export const PAGE_READY = 'page_ready'
export function pageReady(): ActionMessage {
  return {
    message_type: PAGE_READY,
  }
}

export const CONVERT_LAYOUT_RESULT = 'convert_layout_result'
export function convertLayoutResult(results): ActionMessage {
  return {
    message_type: CONVERT_LAYOUT_RESULT,
    ...results,
  }
}

export const UPDATE_ADDRESS = 'padlet_address_update'
export function updateAddress(address: string): ActionMessage {
  return {
    message_type: UPDATE_ADDRESS,
    new_address: address,
  }
}

export const SETTINGS_SAVE_RESULT = 'wall_settings_update_result'
export function saveSettingsResult(results): ActionMessage {
  return {
    message_type: SETTINGS_SAVE_RESULT,
    ...results,
  }
}

interface NativeCopyXferPostResult {
  status: 'success' | 'error'
  copy_type?: string
  transfer_type?: string
  text?: string
  url?: string
}
export const REPORT_COPY_POST = 'copy_post_result'
export function reportCopyPostResult(copyPostResult: NativeCopyXferPostResult): ActionMessage {
  return {
    message_type: REPORT_COPY_POST,
    ...copyPostResult,
  }
}

export const REPORT_TRANSFER_POST = 'transfer_post_result'
export function reportTransferPostResult(transferPostResult: NativeCopyXferPostResult): ActionMessage {
  return {
    message_type: REPORT_TRANSFER_POST,
    ...transferPostResult,
  }
}

const TOUCHEND = 'touchend'
export function firstTouch(): ActionMessage {
  return {
    message_type: TOUCHEND,
    is_first_touch: true,
  }
}

const OPEN_MAP_LOCATION_PICKER = 'open_map_location_picker'
export function openMapLocationPicker(): ActionMessage {
  return {
    message_type: OPEN_MAP_LOCATION_PICKER,
  }
}

const SHOW_COMMENTS_PANEL = 'show_comments_panel'
export function showCommentsPanel(postId: number): ActionMessage {
  return {
    message_type: SHOW_COMMENTS_PANEL,
    post: {
      id: postId,
    },
  }
}

const NATIVE_BRIDGE_READY = 'native_bridge_ready'
export function nativeBridgeReady(): ActionMessage {
  return {
    message_type: NATIVE_BRIDGE_READY,
  }
}

const UPDATE_ACTIVE_CONTRIBUTORS_LIST = 'update_active_contributors_list'
export function updateActiveContributorsList(users: Partial<User>[]): ActionMessage {
  return {
    message_type: UPDATE_ACTIVE_CONTRIBUTORS_LIST,
    users,
  }
}

/**
 * State for the actual padlet page that the native app can have access to.
 */
export interface SyncableSurfaceState {
  wall_sections: Array<Section>
  oauth_token: string
  reactions: Array<Reaction>
  wall_reaction_data: WallReactionData
  expanded_post_data: {
    posts: Array<{ id: Id; cid: Cid; has_attachment: boolean }>
    expanded_post?: Post & { attachment_frame_url: string; comments: Comment[] }
  }
  am_i_collaborator: boolean
  collaborator_just_left_user_id?: UserId
}

const POST_SURFACE_STATE = 'post_surface_state'
export function postSurfaceState(surfaceState: SyncableSurfaceState): ActionMessage {
  return {
    message_type: POST_SURFACE_STATE,
    surface_state: surfaceState,
  }
}

const OPEN_POST_REACTION_DIALOG = 'open_post_reaction_dialog'
export function openPostReactionDialog(postId: Id): ActionMessage {
  return {
    message_type: OPEN_POST_REACTION_DIALOG,
    post_id: postId,
  }
}
